/**
 * useIntegratedSession - 新旧セッション管理の統合フック
 * Requirements: 8.1, 8.2, 8.3
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { QuestionnaireProgress, ProgressAnswer } from '../../../types';
import { getPartNumber } from '../../../constants/partConfig';
import {
  loadCompatibleProgress,
  saveNewProgress,
  clearNewProgress,
  cleanupExpiredProgress,
  getAllSavedProgress,
  getStorageUsage,
  debugStorageData,
} from '../utils/storageCompatibility';

interface UseIntegratedSessionOptions {
  questionSetId: string;
  autoCleanup?: boolean;
  debugMode?: boolean;
}

interface UseIntegratedSessionReturn {
  // 進捗状態
  hasProgress: boolean;
  progress: QuestionnaireProgress | null;
  isLoading: boolean;
  error: string | null;
  
  // 進捗操作
  // answersMap は質問マスタの0-basedインデックスをキーにした回答マップ
  // (74問形式では出題順固定のためランダム化情報は保存しない)
  saveProgress: (answersMap: Record<number, ProgressAnswer>, currentIndex: number) => Promise<void>;
  loadProgress: () => QuestionnaireProgress | null;
  clearProgress: () => Promise<void>;
  
  // セッション管理
  createNewSession: () => string;
  getAllSessions: () => QuestionnaireProgress[];
  cleanupExpiredSessions: () => Promise<{ removedCount: number; errors: string[] }>;
  
  // ストレージ管理
  getStorageInfo: () => {
    used: number;
    total: number;
    percentage: number;
    questionnaireDataSize: number;
  };
  
  // デバッグ
  debugStorage: () => void;
}

export const useIntegratedSession = ({
  questionSetId,
  autoCleanup = true,
  debugMode = false,
}: UseIntegratedSessionOptions): UseIntegratedSessionReturn => {
  const [progress, setProgress] = useState<QuestionnaireProgress | null>(null);
  const [hasProgress, setHasProgress] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 初期化（questionSetIdのみを依存配列に）
  const hasInitialized = useRef(false);
  const prevQuestionSetId = useRef(questionSetId);
  
  useEffect(() => {
    // 同じquestionSetIdで既に初期化済みの場合はスキップ
    if (hasInitialized.current && prevQuestionSetId.current === questionSetId) {
      return;
    }
    
    hasInitialized.current = true;
    prevQuestionSetId.current = questionSetId;

    const initializeSession = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // 自動クリーンアップ
        if (autoCleanup) {
          await cleanupExpiredSessions();
        }

        // 進捗データを読み込み（レガシー移行含む）
        const loadedProgress = loadCompatibleProgress(questionSetId);
        
        if (loadedProgress) {
          setProgress(loadedProgress);
          setHasProgress(true);
          
          if (debugMode) {
            console.log('📋 Loaded progress:', loadedProgress);
          }
        } else {
          setProgress(null);
          setHasProgress(false);
        }

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'セッション初期化に失敗しました';
        setError(errorMessage);
        console.error('Session initialization failed:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeSession();
  }, [questionSetId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 進捗保存
  const saveProgress = useCallback(async (
    answersMap: Record<number, ProgressAnswer>,
    currentIndex: number
  ): Promise<void> => {
    try {
      setError(null);

      // 既存の進捗があればそれを更新、なければ新規作成
      const existingProgress = progress || {
        sessionId: createNewSession(),
        questionSetId,
        currentQuestionIndex: 0,
        totalQuestions: 0,
        answers: {} as Record<number, ProgressAnswer>,
        startedAt: new Date(),
        lastUpdatedAt: new Date(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7日後
        isCompleted: false,
        designVersion: 'redesign' as const,
      };

      // 現在のパート番号を算出 (出題順は固定のため currentIndex がマスタインデックス)
      const currentPartNumber = getPartNumber(currentIndex);

      const updatedProgress: QuestionnaireProgress = {
        ...existingProgress,
        currentQuestionIndex: currentIndex,
        totalQuestions: Math.max(existingProgress.totalQuestions, Object.keys(answersMap).length),
        answers: answersMap,
        lastUpdatedAt: new Date(),
        currentPartNumber,
        // 分析データの更新
        analytics: {
          ...existingProgress.analytics,
          timeSpentPerQuestion: {
            ...existingProgress.analytics?.timeSpentPerQuestion,
            [currentIndex]: Date.now() - (existingProgress.analytics?.timeSpentPerQuestion?.[currentIndex] || Date.now()),
          },
          revisitCount: {
            ...existingProgress.analytics?.revisitCount,
            [currentIndex]: (existingProgress.analytics?.revisitCount?.[currentIndex] || 0) + 1,
          },
          totalTimeSpent: Date.now() - new Date(existingProgress.startedAt).getTime(),
        },
      };

      saveNewProgress(updatedProgress);
      setProgress(updatedProgress);
      setHasProgress(true);

      if (debugMode) {
        console.log('💾 Progress saved:', updatedProgress);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '進捗保存に失敗しました';
      setError(errorMessage);
      console.error('Failed to save progress:', err);
      throw err;
    }
  }, [progress, questionSetId, debugMode]);

  // 進捗読み込み
  const loadProgress = useCallback((): QuestionnaireProgress | null => {
    try {
      const loadedProgress = loadCompatibleProgress(questionSetId);
      
      if (loadedProgress) {
        setProgress(loadedProgress);
        setHasProgress(true);
      }
      
      return loadedProgress;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '進捗読み込みに失敗しました';
      setError(errorMessage);
      console.error('Failed to load progress:', err);
      return null;
    }
  }, [questionSetId]);

  // 進捗クリア
  const clearProgress = useCallback(async (): Promise<void> => {
    try {
      setError(null);
      
      clearNewProgress(questionSetId);
      setProgress(null);
      setHasProgress(false);

      if (debugMode) {
        console.log('🗑️ Progress cleared for:', questionSetId);
      }

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '進捗クリアに失敗しました';
      setError(errorMessage);
      console.error('Failed to clear progress:', err);
      throw err;
    }
  }, [questionSetId, debugMode]);

  // 新しいセッション作成
  const createNewSession = useCallback((): string => {
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    if (debugMode) {
      console.log('🆕 New session created:', sessionId);
    }
    
    return sessionId;
  }, [debugMode]);

  // 全セッション取得
  const getAllSessions = useCallback((): QuestionnaireProgress[] => {
    try {
      return getAllSavedProgress();
    } catch (err) {
      console.error('Failed to get all sessions:', err);
      return [];
    }
  }, []);

  // 期限切れセッションのクリーンアップ
  const cleanupExpiredSessions = useCallback(async (): Promise<{ removedCount: number; errors: string[] }> => {
    try {
      const result = cleanupExpiredProgress();
      
      if (debugMode) {
        console.log('🧹 Cleanup completed:', result);
      }
      
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'クリーンアップに失敗しました';
      console.error('Cleanup failed:', err);
      return { removedCount: 0, errors: [errorMessage] };
    }
  }, [debugMode]);

  // ストレージ情報取得
  const getStorageInfo = useCallback(() => {
    return getStorageUsage();
  }, []);

  // デバッグ情報表示
  const debugStorage = useCallback(() => {
    debugStorageData();
  }, []);

  return {
    // 進捗状態
    hasProgress,
    progress,
    isLoading,
    error,
    
    // 進捗操作
    saveProgress,
    loadProgress,
    clearProgress,
    
    // セッション管理
    createNewSession,
    getAllSessions,
    cleanupExpiredSessions,
    
    // ストレージ管理
    getStorageInfo,
    
    // デバッグ
    debugStorage,
  };
};